"use client";
import { useRouter, usePathname } from 'next/navigation';
import { useEffect } from 'react';

/**
 * Component that listens for location changes and automatically
 * redirects the URL to the new city ONLY when user is already on a city-specific page
 * Example: /mahindra/bolero/price-in-alwar -> /mahindra/bolero/price-in-delhi
 */
export default function LocationURLHandler() {
    const router = useRouter();
    const pathname = usePathname();

    useEffect(() => {
        const handleLocationUpdate = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { displayString, location } = customEvent.detail || {};
            
            // Only handle URL redirection for city-specific brand/model pages
            if (!pathname) return;
            
            const pathParts = pathname.split('/').filter(Boolean);
            
            // Check if we're on a city-specific page: /[brand]/[model]/price-in-[city]
            if (pathParts.length === 3 && pathParts[2].startsWith('price-in-')) {
                const brand = pathParts[0];
                const model = pathParts[1];
                const currentCitySlug = pathParts[2].replace('price-in-', '');
                
                // Get the new city information
                let newCitySlug: string;
                if (location?.slug) {
                    newCitySlug = location.slug;
                } else if (displayString) {
                    const cityOnly = displayString.split(',')[0].trim();
                    newCitySlug = cityOnly.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
                } else {
                    return; // No valid city information
                }
                
                // Skip redirection if it's the same city
                if (currentCitySlug === newCitySlug) {
                    return;
                }
                
                // Build the new URL with the new city
                const newPath = `/${brand}/${model}/price-in-${newCitySlug}`;
                
                try {
                    router.push(newPath);
                } catch (error) {
                    console.error('Failed to redirect to new location URL:', error);
                    // Fallback to window.location
                    window.location.href = newPath;
                }
            }
            // If user is NOT on a city-specific page (e.g., just /mahindra/bolero), 
            // do nothing - no automatic redirection
        };

        window.addEventListener('location-updated', handleLocationUpdate);
        
        return () => {
            window.removeEventListener('location-updated', handleLocationUpdate);
        };
    }, [pathname, router]);

    return null; // This component doesn't render anything
}